import { Injectable } from '@angular/core';

// RxJS
import { BehaviorSubject, catchError, delay, filter, interval, map, merge, Observable, of, startWith, Subject, switchMap, tap, timer } from 'rxjs';

// Services
import { ProjectService } from '@modules/project/services/project.service';
import { NetworkService } from '@modules/core/services/network.service';
import { NotificationService } from '@modules/elements/services/notification.service';

// Types
import { Record, RecordStatus, RecordTask, RecordTaskProgress, RecordTaskType } from '../types/record';
import { Project } from '@modules/project/types/project';
import { RecordEvent } from '../types/record-event';

// Decorators
import { warmUpObservable } from '@decorators';

@Injectable({
  providedIn: 'root'
})
export class RecordsService {

  // Private
  private records = new BehaviorSubject<Record[]>([]);
  private currentProject = new BehaviorSubject<Project | null>(null);
  private recordUpdated = new Subject<string>();
  private recordEventsUpdated = new Subject<string>();
  private taskUpdated = new Subject<string>();
  private taskProgress = new Subject<RecordTaskProgress>();

  /**
   * Constructor
   */

  constructor(
    private projectService: ProjectService,
    private networkService: NetworkService,
    private notificationService: NotificationService,
  ) {
    // Events
    this.networkService.socket?.on("recordUpdated", (data) => {
      console.log('[EVENT][recordUpdated] ', data);
      this.recordUpdated.next(data?.id);
    });
    this.networkService.socket?.on("recordEventsUpdated", (data) => {
      console.log('[EVENT][recordEventsUpdated] ', data);
      this.recordEventsUpdated.next(data?.id);
    });
    this.networkService.socket?.on("taskUpdated", (data) => {
      console.log('[EVENT][taskUpdated] ', data);
      this.taskUpdated.next(data?.id);
    });
    // Fires on every whole per cent of a running task — not logged, it would flood the console
    this.networkService.socket?.on("taskProgress", (data) => {
      this.taskProgress.next(data);
    });
    // Get Current Project
    this.projectService.getCurrentProject().subscribe(project => {
      this.currentProject.next(project);
      this.requestRecords();
    });
  }

  /**
   * Methods
   */

  requestRecords(): void {
    if (this.currentProject.value?.id) {
      this.networkService.getRecords(this.currentProject.value?.id).subscribe(records => this.records.next(records));
    } else {
      this.records.next([]);
    }
  }

  getRecords(): Observable<Record[]> {
    return this.records.asObservable();
  }

  getRecord(id?: string | null): Observable<Record | undefined> {
    return merge(
      this.recordUpdated.asObservable(),
      this.taskUpdated.asObservable(),
      interval(10000).pipe(map(() => id))
    )
      .pipe(
        startWith(id),
        filter(recordId => recordId === id),
        switchMap(() => this.networkService.getRecord(id!)),
        tap(record => console.log('[Record] ', record))
      );
  }

  getRecordUpdated(): Observable<string> {
    return this.recordUpdated.asObservable();
  }

  getTaskProgress(): Observable<RecordTaskProgress> {
    return this.taskProgress.asObservable();
  }

  @warmUpObservable
  createRecord(record: Record): Observable<Record> {
    record.projectId = this.currentProject.value?.id;
    return this.networkService.createRecord(record)
      .pipe(
        tap(record => this.requestRecords())
      );
  }

  @warmUpObservable
  updateRecord(record: Record): Observable<any> {
    return this.networkService.updateRecord(record)
      .pipe(
        tap(record => this.requestRecords())
      );
  }

  @warmUpObservable
  updateRecordStatus(recordId: string, status: RecordStatus): Observable<any> {
    return this.networkService.updateRecordStatus(recordId, status)
      .pipe(
        tap(record => this.requestRecords())
      );
  }

  @warmUpObservable
  cancelRecord(record: Record): Observable<any> {
    if (!record.id) { return of(); }
    return this.networkService.cancelRecord(record.id)
      .pipe(
        tap(record => this.requestRecords())
      );
  }

  @warmUpObservable
  restoreRecord(record: Record): Observable<any> {
    if (!record.id) { return of(); }
    return this.networkService.restoreRecord(record.id)
      .pipe(
        tap(record => this.requestRecords())
      );
  }

  @warmUpObservable
  finishRecord(record: Record): Observable<any> {
    if (!record.id) { return of(); }
    return this.networkService.finishRecord(record.id, true)
      .pipe(
        tap(record => this.requestRecords())
      );
  }

  @warmUpObservable
  deleteRecords(ids: string[]): Observable<any> {
    if (!ids.length) { return of(); }
    return this.networkService.deleteRecords(ids)
      .pipe(
        tap(record => this.requestRecords())
      );
  }

  getRecordEvents(recordId: string): Observable<RecordEvent[]> {
    return this.recordEventsUpdated.asObservable()
      .pipe(
        startWith(recordId),
        switchMap(() => timer(0, 10000)),
        switchMap(() => this.networkService.getEvents(recordId)),
        map(events => events.sort((e1, e2) => new Date(e2.createdAt || 0).getTime() - new Date(e1.createdAt || 0).getTime()))
      );
  }

  @warmUpObservable
  createTasks(recordId: string, serverId: string): Observable<any> {
    return this.networkService.createTasks(recordId, serverId)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Create Tasks: ', message);
          }
          return of();
        })
      );
  }

  @warmUpObservable
  resetRecordTasks(recordId: string, programs: ('main' | 'overtime')[]): Observable<any> {
    return this.networkService.resetRecordTasks(recordId, programs)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Reset Tasks: ', message);
          }
          return of();
        })
      );
  }

  @warmUpObservable
  recreateServerInstance(recordId: string, serverId: string): Observable<any> {
    return this.networkService.recreateServerInstance(recordId, serverId)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Re-create AWS Server: ', message);
          }
          return of();
        })
      );
  }

  /**
   * Actions
   */

  @warmUpObservable
  startRecord(recordId: string, serverIds: string[], recordBody?: any): Observable<any> {
    return this.networkService.startRecord(recordId, serverIds, recordBody)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Start Record', message);
          }
          return of();
        }),
        delay(1000),
        tap(() => this.requestRecords())
      );
  }

  @warmUpObservable
  stopRecord(recordId: string, serverIds: string[]): Observable<any> {
    return this.networkService.stopRecord(recordId, serverIds)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Stop Record', message);
          }
          return of();
        }),
        delay(1000),
        tap(() => this.requestRecords())
      );
  }

  @warmUpObservable
  resetRecord(recordId: string, serverIds: string[]): Observable<any> {
    return this.networkService.resetRecord(recordId, serverIds)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Reset Record', message);
          }
          return of();
        }),
        delay(1000),
        tap(() => this.requestRecords())
      );
  }

  /**
   * Tasks
   */

  @warmUpObservable
  restartTask(recordId: string, serverIds: string[], taskId: string, restartOnlyThis: boolean = false): Observable<any> {
    return this.networkService.restartTasks(recordId, serverIds, taskId, restartOnlyThis)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Restart Task: ', message);
          }
          return of();
        })
      );
  }

  @warmUpObservable
  resetTask(recordId: string, serverIds: string[], taskId: string): Observable<any> {
    return this.networkService.resetTask(recordId, serverIds, taskId)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Reset Task: ', message);
          }
          return of();
        })
      );
  }
  
  @warmUpObservable
  updateTask(serverId: string, task: RecordTask): Observable<any> {
    return this.networkService.updateTask(serverId, task)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Can\'t Update Task: ', message);
          }
          return of();
        })
      );
  }

  @warmUpObservable
  forceDoneTask(recordId: string, serverIds: string[], taskKey: string): Observable<any> {
    return this.networkService.forceDoneTask(recordId, serverIds, taskKey)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Done Task: ', message);
          }
          return of();
        })
      );
  }

  /**
   * Server Actions
   */

  @warmUpObservable
  trim(recordId: string, serverIds: string[], start: string, end: string, keys: RecordTaskType[]): Observable<any> {
    return this.networkService.trim(recordId, serverIds, start, end, keys)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Processing: ', message);
          }
          return of();
        }),
        delay(1000),
      );
  }

  @warmUpObservable
  startManualTask(recordId: string, serverIds: string[], key: string): Observable<any> {
    return this.networkService.startManualTask(recordId, serverIds, key)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Start Upload: ', message);
          }
          return of();
        })
      );
  }

  @warmUpObservable
  s3Download(recordId: string, serverIds: string[], s3VideoUrl: string): Observable<any> {
    return this.networkService.s3Download(recordId, serverIds, s3VideoUrl)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Download Error: ', message);
          }
          return of();
        }),
        delay(1000),
      );
  }

  @warmUpObservable
  startICap(recordId: string, serverIds: string[], start: string, end: string): Observable<any> {
    return this.networkService.startICap(recordId, serverIds, start, end)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Processing: ', message);
          }
          return of();
        }),
        delay(1000),
      );
  }
  
  /**
   * Agent
   */

  @warmUpObservable
  refreshAgentData(recordId: string, serverId: string): Observable<any> {
    return this.networkService.refreshAgentData(recordId, serverId)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Refresh Agent Data: ', message);
          }
          return of();
        })
      );
  }

  @warmUpObservable
  restartAgent(recordId: string, serverId: string): Observable<any> {
    return this.networkService.restartAgent(recordId, serverId)
      .pipe(
        tap(() => this.recordUpdated.next(recordId)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Restart Agent: ', message);
          }
          return of();
        })
      );
  }

}
